import { useState, useRef, useEffect, useCallback, forwardRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlass, Package, ShoppingBag } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import {
  useSearch,
  type OrderSearchResult,
  type ItemSearchResult,
} from '@/api/hooks/useSearch';
import { useActiveWarehouse } from '@/hooks/useActiveWarehouse';
import { useWarehouseStore } from '@/stores/warehouse-store';
import { useAuthStore } from '@/stores/auth-store';
import { cn } from '@/lib/utils';

/** A single flat search result — either an order or an item. */
type SearchResult = OrderSearchResult | ItemSearchResult;

/**
 * GlobalSearchInput
 *
 * Wires the header search box to GET /api/search.
 * - Debounces 300ms before fetching
 * - Only fetches when query >= 2 chars
 * - Keyboard nav: ↓↑ to move highlight, Enter to select, Esc to close
 * - Selecting an order from another warehouse switches the active warehouse
 *   (cache reset + remount), toasts, then navigates (see handleSelectResult)
 * - Cmd+K / Ctrl+K focuses the input from anywhere
 */
export function GlobalSearchInput() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { isAllMode, activeWarehouseId } = useActiveWarehouse();
  const setSelectedWarehouse = useWarehouseStore((s) => s.setSelectedWarehouse);
  const accessibleWarehouses = useWarehouseStore((s) => s.accessibleWarehouses);
  const isAdmin = useAuthStore((s) => s.isAdmin());

  const { data, isFetching } = useSearch(q);

  // Flatten results for keyboard navigation
  type FlatResult =
    | { kind: 'order'; item: OrderSearchResult }
    | { kind: 'item'; item: ItemSearchResult };

  const flatResults: FlatResult[] = [
    ...(data?.orders ?? []).map((o) => ({ kind: 'order' as const, item: o })),
    ...(data?.items ?? []).map((i) => ({ kind: 'item' as const, item: i })),
  ];

  const close = useCallback(() => {
    setOpen(false);
    setSelectedIndex(-1);
  }, []);

  const navigateTo = useCallback(
    (deepLink: string) => {
      navigate(deepLink);
      close();
      setQ('');
    },
    [navigate, close],
  );

  // AD-5: selecting a result that lives in another warehouse switches the
  // active warehouse (header updates + cache reset + remount) BEFORE we
  // navigate, so the destination page loads under the correct scope.
  const handleSelectResult = useCallback(
    (result: SearchResult) => {
      // Item results (no warehouseId field) or orders with a null
      // warehouseId → navigate only, never switch.
      if (!('warehouseId' in result) || result.warehouseId == null) {
        navigateTo(result.deepLink);
        return;
      }

      // Never narrow an admin's "All warehouses" mode, and never switch when
      // the result already lives in the active warehouse.
      if (isAllMode || result.warehouseId === activeWarehouseId) {
        navigateTo(result.deepLink);
        return;
      }

      // Cross-warehouse order. Admins may switch anywhere; non-admins may
      // switch only to a warehouse they can access (defensive — the BE scope
      // already guarantees this). Otherwise navigate WITHOUT switching so we
      // never strand the user in a warehouse they can't see.
      const canSwitch =
        isAdmin ||
        accessibleWarehouses.some((w) => w.warehouseId === result.warehouseId);
      if (!canSwitch) {
        navigateTo(result.deepLink);
        return;
      }

      // Order matters: switch first (triggers cache reset + remount), then
      // toast, then navigate (which also closes the dropdown + clears query).
      setSelectedWarehouse(result.warehouseId);
      toast.success(`Switched to ${result.warehouseName ?? 'warehouse'}`);
      navigateTo(result.deepLink);
    },
    [
      navigateTo,
      isAllMode,
      activeWarehouseId,
      isAdmin,
      accessibleWarehouses,
      setSelectedWarehouse,
    ],
  );

  // Cmd+K / Ctrl+K global shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        close();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [close]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < flatResults.length - 1 ? prev + 1 : prev,
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && flatResults[selectedIndex]) {
          handleSelectResult(flatResults[selectedIndex].item);
        }
        break;
      case 'Escape':
        e.preventDefault();
        close();
        inputRef.current?.blur();
        break;
    }
  };

  const hasResults =
    (data?.orders?.length ?? 0) > 0 || (data?.items?.length ?? 0) > 0;

  return (
    <div className="relative w-full">
      <MagnifyingGlass
        size={18}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10"
      />
      <Input
        ref={inputRef}
        type="search"
        data-testid="global-search"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          setSelectedIndex(-1);
        }}
        onFocus={() => {
          if (q.length >= 2) setOpen(true);
        }}
        onKeyDown={handleKeyDown}
        placeholder="Search orders or items... (⌘K)"
        aria-label="Search orders and items"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-autocomplete="list"
        className="pl-10"
      />
      {open && q.length >= 2 && (
        <SearchResultsDropdown
          ref={dropdownRef}
          orders={data?.orders ?? []}
          items={data?.items ?? []}
          isFetching={isFetching}
          selectedIndex={selectedIndex}
          onSelect={handleSelectResult}
          hasResults={hasResults}
          activeWarehouseId={activeWarehouseId}
          isAllMode={isAllMode}
        />
      )}
    </div>
  );
}

interface SearchResultsDropdownProps {
  orders: OrderSearchResult[];
  items: ItemSearchResult[];
  isFetching: boolean;
  selectedIndex: number;
  onSelect: (result: SearchResult) => void;
  hasResults: boolean;
  activeWarehouseId: string | null;
  isAllMode: boolean;
}

const SearchResultsDropdown = forwardRef<
  HTMLDivElement,
  SearchResultsDropdownProps
>(
  (
    {
      orders,
      items,
      isFetching,
      selectedIndex,
      onSelect,
      hasResults,
      activeWarehouseId,
      isAllMode,
    },
    ref,
  ) => {
    let globalIdx = 0;

    return (
      <div
        ref={ref}
        role="listbox"
        aria-label="Search results"
        className="absolute left-0 top-full mt-1 w-full min-w-[320px] rounded-lg border border-border bg-background shadow-lg z-50 max-h-[400px] overflow-y-auto"
        data-testid="search-results-dropdown"
      >
        {isFetching && !hasResults && (
          <div className="px-4 py-3 text-sm text-muted-foreground">
            Searching…
          </div>
        )}

        {!isFetching && !hasResults && (
          <div className="px-4 py-3 text-sm text-muted-foreground">
            No results found
          </div>
        )}

        {orders.length > 0 && (
          <div>
            <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b border-border">
              Orders
            </div>
            {orders.map((order) => {
              const idx = globalIdx++;
              // This order lives in a different warehouse than the active one
              // (and we're not in admin "All" mode, where selecting never
              // narrows scope). Selecting it usually switches the active
              // warehouse — except a non-admin without access, who just
              // navigates.
              const showWarehouseBadge =
                !isAllMode &&
                order.warehouseId != null &&
                order.warehouseId !== activeWarehouseId;
              return (
                <button
                  key={order.id}
                  role="option"
                  aria-selected={selectedIndex === idx}
                  onClick={() => onSelect(order)}
                  className={cn(
                    'flex w-full items-center gap-3 px-3 py-2.5 text-sm text-left hover:bg-accent/50 transition-colors',
                    selectedIndex === idx && 'bg-accent',
                  )}
                  data-testid={`search-result-order-${order.id}`}
                >
                  <ShoppingBag
                    size={16}
                    className="shrink-0 text-muted-foreground"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {order.orderNumber}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {order.customerName} · {order.status}
                    </div>
                    {showWarehouseBadge && (
                      <span
                        className="mt-1 inline-block rounded border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                        data-testid={`search-result-order-warehouse-${order.id}`}
                      >
                        {order.warehouseName}
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {items.length > 0 && (
          <div>
            <div className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b border-border">
              Items
            </div>
            {items.map((item) => {
              const idx = globalIdx++;
              return (
                <button
                  key={item.id}
                  role="option"
                  aria-selected={selectedIndex === idx}
                  onClick={() => onSelect(item)}
                  className={cn(
                    'flex w-full items-center gap-3 px-3 py-2.5 text-sm text-left hover:bg-accent/50 transition-colors',
                    selectedIndex === idx && 'bg-accent',
                  )}
                  data-testid={`search-result-item-${item.id}`}
                >
                  <Package
                    size={16}
                    className="shrink-0 text-muted-foreground"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{item.sku}</div>
                    <div className="text-xs text-muted-foreground truncate">
                      {item.name}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  },
);
SearchResultsDropdown.displayName = 'SearchResultsDropdown';
